import React from "react"

// Helpers
import {lineNameFor, roundNumber, offBpuCoefficient} from '../../helpers'

export default class Inputs extends React.Component {
  constructor(props) {
    super(props)
    this.onChange  = this.onChange.bind(this)
    this.changeVat = this.changeVat.bind(this)
  }

  onChange(e, attribute) {
    e.preventDefault()
    const value = e.target.value
    const { index, replaceLine, qline, quote, overheadCoef } = this.props

    if (attribute == "purchase_price") {
      const purchase_price = value
      const pa             = parseFloat(String(purchase_price).replace(",", ".")) || 0

      // Marché-linked quote, non-BPU line, contract has off-BPU tiers:
      // tier coefficient takes priority over the generic markup setting.
      const useTier = quote.mbc_contract?.id && qline.type !== "bpu" &&
                      (quote.mbc_off_bpu_tiers || []).length > 0
      if (useTier) {
        const coef = offBpuCoefficient(pa, quote.mbc_off_bpu_tiers)
        if (coef != null) {
          const unit_price = roundNumber(pa * coef)
          replaceLine({...qline, purchase_price, unit_price}, index)
          return
        }
        // No tier matched → leave unit_price untouched.
        replaceLine({...qline, purchase_price}, index)
        return
      }

      // If the line's category has a sale coef APPLIED on the quote,
      // PV = PA × (1 + overhead coef_cat) × sale coef_cat — consistent with what
      // the summary's "Apply" click set. Otherwise historical fallback
      // on the price_markup_coefficient (if enabled).
      const cat            = qline.cost_category
      const saleOverrides  = (quote && quote.sale_coef_overrides) || {}
      const venteOver      = cat ? saleOverrides[cat] : null
      const venteParsed    = venteOver != null && venteOver !== ""
                               ? parseFloat(String(venteOver).replace(",", "."))
                               : NaN
      if (pa > 0 && Number.isFinite(venteParsed) && venteParsed > 0) {
        const fg         = parseFloat(String(overheadCoef ?? 0).replace(",", ".")) || 0
        const unit_price = roundNumber(pa * (1 + fg) * venteParsed)
        replaceLine({...qline, purchase_price, unit_price}, index)
        return
      }
      if (quote.allow_markup) {
        const unit_price = roundNumber(pa * quote.price_markup_coefficient)
        replaceLine({...qline, purchase_price, unit_price}, index)
        return
      }
    }

    replaceLine({...qline, [attribute]: value}, index)
  }

  changeVat(e) {
    e.preventDefault()
    const { index, replaceLine, qline, vats } = this.props
    const vat = vats.find((vat) => vat.id == e.target.value)
    replaceLine({...qline, vat_rate: vat || {}}, index)
  }

  render() {
    const { quote, qline, vats, index, showMargin, useRevient, overheadCoef, readOnlyPrices } = this.props

    const minimalQuantityTooltip = qline.can_be_deleted
        ? null
        : I18n.t("quotes.new.quantity_minimal", { min: qline.quantity_invoiced })

    // When the cost-breakdown preference is on AND the line carries a cost
    // category, expose a read-only "Coût de revient" right after the purchase price.
    const showRevient = !!useRevient && !!showMargin
    const pa          = parseFloat(qline.purchase_price) || 0
    const revient     = pa * (1 + (parseFloat(overheadCoef) || 0))
    const colCount    = 6 + (showMargin ? 1 : 0) + (showRevient ? 1 : 0)

    return(
      <div className="row-inputs" style={{gridTemplateColumns: `repeat(${colCount},  1fr)`}}>
        <div>
          <label>{I18n.t("quotes.bigcard.reference")}</label>

          <input className="form-control"
            value={qline.ref_product_distrib || ""}
            onChange={e => this.onChange(e, "ref_product_distrib")}
            name={lineNameFor("ref_product_distrib", index)}
          />
        </div>

        <div {...(minimalQuantityTooltip ? { tooltip: minimalQuantityTooltip, tooltip_position: "top" } : {})}>
          <label required>{I18n.t("quotes.new.quantity")}</label>

          <input className="form-control text-right"
            type     = "number"
            min      = {qline.quantity_invoiced}
            step     = "0.01"
            value    = {qline.quantity ?? ""}
            onChange = {e => this.onChange(e, "quantity")}
            name     = {lineNameFor("quantity", index)}
            required
          />
        </div>

        <div>
          <label>{I18n.t("quotes.bigcard.unit")}</label>

          <input className="form-control"
            value={qline.unit || ""}
            onChange={e => this.onChange(e, "unit")}
            name={lineNameFor("unit", index)}
          />
        </div>

        { showMargin &&
          <>
            <div>
              <label>{I18n.t("quotes.new.purchase_price")}</label>

              <input className="form-control text-right"
                type="number"
                step="0.01"
                value={qline.purchase_price || ""}
                disabled={quote.read_only_purchase_price}
                readOnly={readOnlyPrices}
                onChange={e => this.onChange(e, "purchase_price")}
                name={lineNameFor("purchase_price", index)}
              />
            </div>
          </>
        }

        { showRevient &&
          <div>
            <label>{I18n.t("quotes.bigcard.cost_price")}</label>

            <input className="form-control text-right"
              type     = "text"
              value    = {revient > 0 ? roundNumber(revient) : ""}
              disabled
              readOnly
              title    = {I18n.t("quotes.new.service_cost_price_tooltip")}
            />
          </div>
        }

        <div>
          <label required>{I18n.t("quotes.new.sale_price")}</label>

          <input className="form-control text-right"
            type     = "number"
            step     = "0.01"
            value    = {qline.unit_price || ""}
            readOnly = {readOnlyPrices}
            onChange = {e => this.onChange(e, "unit_price")}
            name     = {lineNameFor("unit_price", index)}
            required
          />
        </div>

        <div>
          <label required>{I18n.t("quotes.bigcard.vat")}</label>

          <select
            className="form-control height-41"
            value={qline.vat_rate.id || ""}
            onChange={this.changeVat}
            name={lineNameFor("vat_rate_id", index)}
            disabled={quote.read_only_vat}
            required
          >
            <option value="">{I18n.t("sites.select_vat")}</option>
            { vats.map((vat, vatIndex) =>
                <option key={vatIndex} value={vat.id}>{vat.label}</option>
              )
            }
          </select>
        </div>

        <div>
          <label>{I18n.t("quotes.new.discount")}</label>

          <input className="form-control text-right"
            type="number"
            step="0.01"
            max="100"
            value={qline.discount || ""}
            onChange={e => this.onChange(e, "discount")}
            name={lineNameFor("discount", index)}
          />
        </div>
      </div>
    )
  }
}
